/**
 * THE CHART — recharts, which is already a direct dependency and already in the
 * shipped bundle (`pages/growth/index.tsx`), so the agent surface adds no new
 * front-end dependency at all.
 *
 * ══ THE FORM IS CHOSEN FROM THE ENVELOPE, NEVER FROM THE MODEL ═════════════
 *
 * `chooseChart` reads `columns[].role` and the row count. The assistant has no
 * say: it cannot request a form, and it cannot put a value on this canvas,
 * because the only value path is `chartValue`, which maps EVERY non-number —
 * suppressed cell, null, string — to `null`. Recharts draws a BREAK in the
 * series for null. A zero would draw a plotted point at the origin, which is a
 * measurement of something the disclosure engine declined to serve.
 *
 * ══ THE TABLE IS THE ACCESSIBLE ALTERNATIVE ════════════════════════════════
 *
 * The figure is `role="img"` with a label, and the table beside it carries the
 * same numbers in a form a screen reader can walk. Spinners and animations use
 * `motion-safe:` so they freeze under `prefers-reduced-motion`.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ChartPlan } from '../answer-view';
import type { ColumnFormat } from '../protocol';
import { formatMeasure } from '../answer-view';

/**
 * THE TOOLTIP MUST BE THEMED OR IT IS WHITE-ON-WHITE IN DARK MODE.
 *
 * Recharts' default tooltip paints an opaque WHITE content box and takes each
 * item's colour from its series stroke. Those strokes are theme tokens, so in
 * dark mode series 0 (`--primary`) is near-white — near-white text on a white
 * box. On DEV the operator saw a solid white rectangle with only the
 * `--destructive` (red) series legible inside it.
 *
 * The axes, grid and series were all themed deliberately ("so dark mode needs
 * no extra code"); the tooltip is the one part Recharts renders for you, so it
 * was the one part nobody themed. Every other chart in this admin panel already
 * sets `contentStyle` — `pages/growth/index.tsx` and
 * `pages/launch-command-center/index.tsx` — so this chart was the outlier, not
 * the precedent.
 *
 * `itemStyle` is DELIBERATELY NOT SET: each item's colour is its series colour,
 * which is what ties a row of the tooltip to a line on the chart. Overriding it
 * to a single foreground colour would make the tooltip readable and the legend
 * meaningless.
 */
const TOOLTIP_CONTENT_STYLE = {
  backgroundColor: 'hsl(var(--popover))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '0.375rem',
} as const;

/** The x-value heading. It has no series, so it needs its own colour. */
const TOOLTIP_LABEL_STYLE = { color: 'hsl(var(--foreground))' } as const;

/** Chart series colours, from the theme tokens so dark mode needs no extra code. */
const SERIES_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--muted-foreground))',
  'hsl(var(--destructive))',
];

/**
 * A PIVOTED chart draws one series per CATEGORY (a channel, a status), up to
 * six, and the three theme tokens above cannot tell six apart. These are six
 * distinct hues at the 600 shade, each at least 3:1 against both the light
 * and the dark card background (WCAG 1.4.11, graphical objects), so no series
 * disappears in either theme. The legend names each one.
 */
const CATEGORY_COLORS = ['#2563eb', '#d97706', '#059669', '#e11d48', '#7c3aed', '#0891b2'];

export function ResultChart({ plan, title }: { plan: ChartPlan; title: string }) {
  /**
   * 🔴 PER SERIES, NEVER series[0]. This was
   *     const format = plan.series[0]?.format ?? 'integer';
   * applied to EVERY series in both tooltips. On
   * `revenue.ltv_by_channel_cohort` series 0 is `revenue_cents`, so hovering
   * the `paying_members` line on 42 members rendered `$0.42` — while the table
   * three lines below rendered `42`. Two different numbers for one cell, and
   * the wrong one carried a currency symbol it had no right to.
   *
   * The per-series format was already on the plan (`answer-view.ts` builds
   * `series: measures.map((m) => ({ key, label, format: m.format }))`); the
   * chart simply threw it away. Recharts hands the formatter the payload item,
   * whose `dataKey` is the series key — that is the lookup.
   */
  const formatOf = (key: unknown): ColumnFormat =>
    plan.series.find((s) => s.key === key)?.format ?? 'integer';

  /**
   * The Y axis carried NO formatter while the tooltip and the table both did,
   * so `revenue_cents` was drawn in raw cents beside a tooltip reading dollars
   * and a table reading dollars — the axis disagreeing with everything else on
   * the same figure.
   *
   * Only formatted when EVERY series shares one format. With mixed units there
   * is no honest single axis label, and formatting by the first series would
   * reintroduce the exact defect fixed above one element to the left.
   */
  const axisFormat: ColumnFormat | null =
    plan.series.length > 0 && plan.series.every((x) => x.format === plan.series[0].format)
      ? plan.series[0].format
      : null;
  const yTickFormatter =
    axisFormat === null ? undefined : (v: number) => formatMeasure(v, axisFormat);
  const axisTick = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };
  // A count axis takes whole-number ticks only. Recharts placed ticks at 1.5
  // and 2.5, the integer formatter printed both as "2", and the PREDEV battery
  // (2026-09-24) showed an axis reading 0, 1, 2, 2, 2.
  const allowDecimals = !(axisFormat === 'integer' || axisFormat === 'currency_cents');
  const colorAt = (index: number): string =>
    plan.pivot
      ? CATEGORY_COLORS[index % CATEGORY_COLORS.length]
      : SERIES_COLORS[index % SERIES_COLORS.length];
  const legend = plan.pivot ? (
    <Legend wrapperStyle={{ fontSize: 11, color: 'hsl(var(--muted-foreground))' }} />
  ) : null;

  return (
    <figure role="img" aria-label={`${title}. The table below carries the same values.`}>
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {plan.kind === 'line' ? (
            <LineChart data={plan.points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey={plan.xKey} tick={axisTick} tickLine={false} />
              <YAxis tick={axisTick} tickLine={false} width={56} tickFormatter={yTickFormatter} allowDecimals={allowDecimals} />
              <RechartsTooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                formatter={(value: unknown, _name: unknown, item?: { dataKey?: unknown }) =>
                  typeof value === 'number'
                    ? formatMeasure(value, formatOf(item?.dataKey))
                    : '—'
                }
              />
              {legend}
              {plan.series.map((series, index) => (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  name={series.label}
                  stroke={colorAt(index)}
                  strokeWidth={2}
                  dot={false}
                  // A withheld cell is a HOLE in the series, and it must LOOK
                  // like one. `connectNulls` would draw straight through it and
                  // invent a trend across a number nobody was served.
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          ) : (
            <BarChart data={plan.points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey={plan.xKey} tick={axisTick} tickLine={false} />
              <YAxis tick={axisTick} tickLine={false} width={56} tickFormatter={yTickFormatter} allowDecimals={allowDecimals} />
              <RechartsTooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                formatter={(value: unknown, _name: unknown, item?: { dataKey?: unknown }) =>
                  typeof value === 'number'
                    ? formatMeasure(value, formatOf(item?.dataKey))
                    : '—'
                }
              />
              {legend}
              {plan.series.map((series, index) => (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  name={series.label}
                  fill={colorAt(index)}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      <figcaption className="mt-1 text-[11px] text-muted-foreground">
        {plan.pivot
          ? `${plan.pivot.measureLabel} by ${plan.xLabel} and ${plan.pivot.seriesLabel}`
          : `${plan.xLabel} · ${plan.series.map((s) => s.label).join(', ')}`}{' '}
        · a break in the series is a withheld or missing value, never a zero.
      </figcaption>
    </figure>
  );
}
